import { useState, useCallback } from "react";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel/TextAreaWithLabel";
import { useReviewDisclaimer } from "../../hooks/useReviewDisclaimer";

interface ConflictReviewModalProps {
	conflictDetails: string;
	evalId: number;
	isOpen: boolean;
	memberId: number;
	memberEmail?: string;
	memberName: string;
	/** Read-only view of an already-reviewed conflict */
	mode?: "review" | "view";
	reviewAction?: "accepted" | "revoked" | null;
	reviewNotes?: string | null;
	reviewedAt?: string | null;
	rfxId: number;
	onClose: () => void;
	onReviewed?: () => void;
}

const DEFAULT_NOTES = "Conflict of interest reported";

export const ConflictReviewModal: React.FC<ConflictReviewModalProps> = ({
	isOpen,
	onClose,
	onReviewed,
	rfxId,
	evalId,
	memberId,
	memberName,
	memberEmail,
	conflictDetails,
	mode = "review",
	reviewAction,
	reviewNotes,
	reviewedAt,
}) => {
	const [notes, setNotes] = useState(DEFAULT_NOTES);
	const reviewMutation = useReviewDisclaimer(rfxId, evalId);
	const isViewMode = mode === "view";

	const isDisabled = notes.trim().length === 0;

	const handleReview = useCallback(
		(action: "accepted" | "revoked") => {
			reviewMutation.mutate(
				{
					memberId,
					payload: { action, notes: notes.trim() },
				},
				{
					onSuccess: () => {
						onReviewed?.();
						onClose();
					},
				}
			);
		},
		[reviewMutation, memberId, notes, onClose, onReviewed]
	);

	const formatDate = (dateStr: string | null | undefined): string => {
		if (!dateStr) {
			return "-";
		}
		return new Date(dateStr).toLocaleDateString("en-AU", {
			day: "numeric",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const footer = isViewMode ? (
		<div className="flex justify-end">
			<Button
				btntype="secondary"
				onClick={onClose}
				data-testid="conflict-view-close-btn"
			>
				Close
			</Button>
		</div>
	) : (
		<div className="flex justify-end gap-2">
			<Button
				btntype="primary"
				onClick={() => handleReview("accepted")}
				disabled={isDisabled}
				isLoading={reviewMutation.isPending}
				data-testid="conflict-review-accept-btn"
			>
				Accept Conflict
			</Button>
			<Button
				btntype="danger"
				onClick={() => handleReview("revoked")}
				disabled={isDisabled}
				isLoading={reviewMutation.isPending}
				data-testid="conflict-review-revoke-btn"
			>
				Revoke Access
			</Button>
		</div>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isViewMode ? "Conflict of Interest — Review Details" : "Review Conflict of Interest"}
			iconType="warning"
			size="xl"
			customFooter={footer}
		>
			<div className="space-y-4" data-testid="conflict-review-modal-content">
				<div>
					<p className="mb-1 text-xs font-medium text-gray-500">Panel Member</p>
					<p
						className="text-sm font-medium text-gray-900"
						data-testid="conflict-review-member-name"
					>
						{memberName}
					</p>
					{memberEmail && (
						<p className="text-xs text-gray-500">{memberEmail}</p>
					)}
				</div>

				<div>
					<p className="mb-1 text-xs font-medium text-gray-500">
						Conflict Details
					</p>
					<div
						className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
						data-testid="conflict-review-details"
					>
						{conflictDetails || <span className="italic text-gray-400">No details provided</span>}
					</div>
				</div>

				{isViewMode ? (
					<>
						<div>
							<p className="mb-1 text-xs font-medium text-gray-500">Review Decision</p>
							<span
								className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
									reviewAction === "accepted"
										? "bg-green-100 text-green-700"
										: "bg-red-100 text-red-700"
								}`}
								data-testid="conflict-review-action-badge"
							>
								{reviewAction === "accepted" ? "Accepted" : "Revoked"}
							</span>
						</div>
						{reviewNotes && (
							<div>
								<p className="mb-1 text-xs font-medium text-gray-500">
									Reviewer Notes
								</p>
								<div
									className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
									data-testid="conflict-review-notes-display"
								>
									{reviewNotes}
								</div>
							</div>
						)}
						{reviewedAt && (
							<div>
								<p className="mb-1 text-xs font-medium text-gray-500">Reviewed At</p>
								<p className="text-sm text-gray-700" data-testid="conflict-review-date">
									{formatDate(reviewedAt)}
								</p>
							</div>
						)}
					</>
				) : (
					<TextAreaWithLabel
						label="Notes"
						id="conflict-review-notes"
						required
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						hasError={notes.trim().length === 0}
						errorText={notes.trim().length === 0 ? "Notes are required" : undefined}
						rows={4}
						noMargin
						showCharacterCount={false}
						testId="conflict-review-notes-textarea"
					/>
				)}
			</div>
		</Modal>
	);
};
